import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SystemDetailsModal } from "./SystemDetailsModal";
import { motion } from "framer-motion";
import { Heart, Brain, Activity, Droplet, Zap, Eye, AlertCircle } from "lucide-react";

interface HealthSystem {
  name: string;
  status: "excellent" | "good" | "attention" | "critical";
  metrics: number;
  alerts: number;
  summary?: string;
  detailed_metrics?: Array<{
    name: string;
    value: string | number;
    unit?: string;
    status: "normal" | "abnormal";
    reference?: string;
  }>;
}

interface HealthSystemMapProps {
  systems: HealthSystem[];
}

const systemIcons: Record<string, any> = {
  "cardiovascular": Heart,
  "neurológico": Brain,
  "metabólico": Activity,
  "hematológico": Droplet,
  "endócrino": Zap,
  "oftalmológico": Eye,
};

const statusConfig = {
  excellent: { color: "bg-emerald-500", label: "Excelente", textColor: "text-emerald-700" },
  good: { color: "bg-blue-500", label: "Bom", textColor: "text-blue-700" },
  attention: { color: "bg-amber-500", label: "Atenção", textColor: "text-amber-700" },
  critical: { color: "bg-red-500", label: "Crítico", textColor: "text-red-700" }
};

export function HealthSystemMap({ systems }: HealthSystemMapProps) {
  const [selectedSystem, setSelectedSystem] = useState<HealthSystem | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleSystemClick = (system: HealthSystem) => {
    setSelectedSystem(system);
    setShowModal(true);
  };

  return (
    <>
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-primary" />
            Mapa de Sistemas de Saúde
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {systems.map((system, index) => {
              const Icon = systemIcons[system.name.toLowerCase()] || Activity;
              const config = statusConfig[system.status];
              
              return (
                <motion.div
                  key={system.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card 
                    className="relative overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => handleSystemClick(system)}
                  >
                    <div className={`absolute top-0 left-0 w-full h-1 ${config.color}`}></div>
                    
                    <CardContent className="pt-6 pb-4">
                      <div className="flex items-start justify-between mb-3">
                        <Icon className={`h-6 w-6 ${config.textColor}`} />
                        {system.alerts > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {system.alerts}
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-sm mb-2 capitalize">{system.name}</h3>
                      
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={`text-xs ${config.textColor}`}>
                          {config.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {system.metrics} métricas
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {selectedSystem && (
        <SystemDetailsModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedSystem(null);
          }}
          system={selectedSystem}
        />
      )}
    </>
  );
}
